import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { PowerSyncDatabase } from '@powersync/web';
import type { PowerSyncBackendConnector, Schema } from '@powersync/web';

// ─── Schema Version Tracking ───
// When tables are added/removed from the PowerSync schema, existing clients have
// a stale local database. We detect this by hashing the schema's table names and
// clearing the local DB when the hash changes, forcing a full re-sync.

const SCHEMA_VERSION_KEY = 'powercareai:ps-schema-version';

function computeSchemaVersion(schema: Schema): string {
  // Sort table names for deterministic hash
  const tables = Object.keys(schema.tables ?? {}).sort();
  return tables.join(',');
}

function schemaChanged(schema: Schema, dbFilename: string): boolean {
  const current = computeSchemaVersion(schema);
  const storageKey = `${SCHEMA_VERSION_KEY}:${dbFilename}`;
  const stored = localStorage.getItem(storageKey);

  if (stored === current) return false;

  console.log('[PowerSync] Schema version changed', {
    stored: stored ?? '(none)',
    current,
    dbFilename,
  });
  return true;
}

function saveSchemaVersion(schema: Schema, dbFilename: string): void {
  const storageKey = `${SCHEMA_VERSION_KEY}:${dbFilename}`;
  localStorage.setItem(storageKey, computeSchemaVersion(schema));
}

export interface PowerSyncContextValue {
  db: PowerSyncDatabase | null;
  connector: PowerSyncBackendConnector | null;
  isConnected: boolean;
  isSyncing: boolean;
  error: Error | null;
}

const PowerSyncContext = createContext<PowerSyncContextValue>({
  db: null,
  connector: null,
  isConnected: false,
  isSyncing: false,
  error: null,
});

export interface PowerSyncProviderProps {
  children: ReactNode;
  powerSyncUrl: string;
  schema: Schema;
  dbFilename?: string;
  /** Enable multi-tab support (shared OPFS lock). Defaults to false. */
  enableMultiTabs?: boolean;
  /** Factory that creates the backend connector. Called once per init. */
  createConnector: (db: PowerSyncDatabase) => PowerSyncBackendConnector;
}

/**
 * Provides PowerSync database instance to the React tree.
 * Initializes connection after mount and handles sync state.
 */
export function PowerSyncProvider({
  children,
  powerSyncUrl,
  schema,
  dbFilename = 'powersync-web-admin.db',
  enableMultiTabs = false,
  createConnector,
}: PowerSyncProviderProps) {
  const [db, setDb] = useState<PowerSyncDatabase | null>(null);
  const [connector, setConnector] = useState<PowerSyncBackendConnector | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let database: PowerSyncDatabase | null = null;
    let mounted = true;

    async function init() {
      try {
        console.log('[PowerSync] Starting initialization...', { powerSyncUrl });

        database = new PowerSyncDatabase({
          schema,
          database: { dbFilename },
          flags: { enableMultiTabs },
        });
        console.log('[PowerSync] Database instance created');

        // Bail early if unmounted (React StrictMode cleanup)
        if (!mounted) {
          console.log('[PowerSync] Unmounted before init — aborting');
          return;
        }

        console.log('[PowerSync] Calling database.init()...');
        await database.init();
        console.log('[PowerSync] Database initialized successfully');

        // Bail early if unmounted during async init
        if (!mounted) {
          console.log('[PowerSync] Unmounted during init — aborting');
          return;
        }

        // If the schema has changed (tables added/removed), clear the local
        // database so PowerSync does a full re-sync with the new schema.
        if (schemaChanged(schema, dbFilename)) {
          console.log('[PowerSync] Clearing stale local database for schema migration...');
          await database.disconnectAndClear({ clearLocal: true });
          console.log('[PowerSync] Local database cleared — will re-sync from server');
        }

        // Persist the current schema version after potential clear
        saveSchemaVersion(schema, dbFilename);

        // Make db available for local reads/writes immediately after init
        setDb(database);
        setError(null);
        console.log('[PowerSync] Local database ready');

        // Register listener BEFORE connect() so we catch all status transitions
        const conn = createConnector(database);
        if (mounted) setConnector(conn);

        database.registerListener({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          statusChanged: (status: any) => {
            console.log(
              '[PowerSync] Status changed:',
              JSON.stringify({
                connected: status.connected,
                connecting: status.connecting,
                dataFlow: status.dataFlowStatus,
                hasSynced: status.hasSynced,
                lastSyncedAt: status.lastSyncedAt,
              }),
            );
            if (mounted) {
              setIsConnected(status.connected);
              setIsSyncing(status.dataFlowStatus?.downloading);
            }
          },
        });

        console.log('[PowerSync] Calling database.connect()...');
        await database.connect(conn);
        console.log('[PowerSync] connect() resolved');

        // Auto-recovery: if connection settles with upload errors, retry once after a delay.
        // This handles cold-start failures where the initial upload fails but the function
        // is warm by the time we retry.
        setTimeout(async () => {
          if (!mounted || !database) return;
          const status = database.currentStatus;
          const uploadErr = status?.dataFlowStatus?.uploadError;
          if (uploadErr && status?.connected) {
            console.log(
              '[PowerSync] Auto-recovery: upload error detected after connect, reconnecting...',
            );
            try {
              await database.disconnect();
              await new Promise((r) => setTimeout(r, 2000));
              if (mounted) await database.connect(conn);
              console.log('[PowerSync] Auto-recovery: reconnected');
            } catch (retryErr) {
              console.warn('[PowerSync] Auto-recovery failed:', retryErr);
            }
          }
        }, 8000);
      } catch (err) {
        console.error('[PowerSync] Initialization failed:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsConnected(false);
        }
      }
    }

    init();

    return () => {
      mounted = false;
      if (database) {
        console.log('[PowerSync] Cleaning up - disconnecting and closing database');
        database.disconnect();
        database.close();
      }
    };
  }, [powerSyncUrl, schema, dbFilename, enableMultiTabs, createConnector]);

  return (
    <PowerSyncContext.Provider value={{ db, connector, isConnected, isSyncing, error }}>
      {children}
    </PowerSyncContext.Provider>
  );
}

/**
 * Hook to access PowerSync database and connection state.
 */
export function usePowerSyncDb(): PowerSyncContextValue {
  const context = useContext(PowerSyncContext);
  if (!context) {
    throw new Error('usePowerSyncDb must be used within a PowerSyncProvider');
  }
  return context;
}
